(function () {
  const STORAGE_KEY = 'yerba:cart-discount-state';
  const NEWSLETTER_COUPON_ELIGIBILITY_KEY = 'yerba:newsletter-coupon-eligible';
  const CHANGE_EVENT = 'yerba:discount-state-changed';
  const CART_EVENTS = ['cart:updated', 'theme:cart:updated', 'ajaxCart:updated'];
  const DAY_MS = 24 * 60 * 60 * 1000;
  const config = window.yerbaDiscountConfig || {};

  const normalize = (value) => (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizeCode = (value) => (value || '').toString().trim();
  const getNow = () => Date.now();

  const getTtlMs = () => {
    const ttl = Number.parseInt(config.ttlMs, 10);
    return Number.isFinite(ttl) && ttl > 0 ? ttl : 7 * DAY_MS;
  };

  const parsePercent = (value) => {
    const percent = Number.parseFloat(value || '0');
    return Number.isFinite(percent) && percent > 0 ? Math.min(100, percent) : 0;
  };

  const getStorage = () => {
    try {
      return window.sessionStorage || null;
    } catch (error) {
      return null;
    }
  };

  const rememberNewsletterCouponEligibility = () => {
    const storage = getStorage();
    if (!storage) return false;

    try {
      storage.setItem(NEWSLETTER_COUPON_ELIGIBILITY_KEY, 'true');
      return true;
    } catch (error) {
      return false;
    }
  };

  const readStoredState = () => {
    const storage = getStorage();
    if (!storage) return null;

    try {
      const state = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
      if (!state || typeof state !== 'object') return null;

      const percent = parsePercent(state.percent);
      const expiresAt = Number.parseInt(state.expiresAt, 10);

      if (!percent || !Number.isFinite(expiresAt) || expiresAt <= getNow()) {
        storage.removeItem(STORAGE_KEY);
        return null;
      }

      return {
        code: normalizeCode(state.code),
        percent,
        source: normalizeCode(state.source),
        savedAt: Number.parseInt(state.savedAt, 10) || getNow(),
        expiresAt
      };
    } catch (error) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  const writeStoredState = (state) => {
    const storage = getStorage();
    if (!storage) return false;

    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeStoredState = () => {
    const storage = getStorage();
    if (!storage) return false;

    try {
      storage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      return false;
    }
  };

  const normalizeOptions = (options) => {
    if (typeof options === 'string') {
      return { source: options };
    }

    return options && typeof options === 'object' ? options : {};
  };

  const statesMatch = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;

    return normalizeCode(a.code) === normalizeCode(b.code)
      && parsePercent(a.percent) === parsePercent(b.percent);
  };

  const dispatchChange = (state, previousState, options) => {
    if (options && options.silent) return;
    if (statesMatch(state, previousState)) return;

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      bubbles: true,
      detail: {
        state,
        previousState,
        source: normalizeCode(options && options.source)
      }
    }));
  };

  const remember = (input, options) => {
    const opts = normalizeOptions(options);
    const previousState = readStoredState();
    const percent = parsePercent(input && input.percent);

    if (!percent) return previousState;

    const now = getNow();
    const state = {
      code: normalizeCode(input && input.code),
      percent,
      source: normalizeCode((input && input.source) || opts.source || 'manual'),
      savedAt: now,
      expiresAt: now + getTtlMs()
    };

    writeStoredState(state);
    dispatchChange(state, previousState, opts);

    return state;
  };

  const clear = (options) => {
    const opts = normalizeOptions(options);
    const previousState = readStoredState();

    removeStoredState();
    dispatchChange(null, previousState, opts);

    return null;
  };

  const unwrapDiscountApplication = (entry) => {
    if (!entry) return null;

    return entry.discount_application
      || entry.discountApplication
      || entry;
  };

  const getCartDiscountApplications = (cart) => {
    if (!cart || typeof cart !== 'object') return [];

    const applications = []
      .concat(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [])
      .concat(Array.isArray(cart.discount_applications) ? cart.discount_applications : [])
      .map(unwrapDiscountApplication)
      .filter(Boolean);

    const items = Array.isArray(cart.items) ? cart.items : [];
    items.forEach((item) => {
      const allocations = Array.isArray(item && item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];

      allocations.forEach((allocation) => {
        const application = unwrapDiscountApplication(allocation);
        if (application) applications.push(application);
      });
    });

    return applications;
  };

  const isCouponDiscountApplication = (application) => {
    if (!application) return false;

    const type = normalize(application.type || application.discount_application_type || '');
    return type === 'discount_code';
  };

  const isProductDiscountApplication = (application) => {
    if (!isCouponDiscountApplication(application)) return false;

    const targetType = normalize(application.target_type || application.targetType || '');
    return targetType !== 'shipping_line';
  };

  const getApplicationCode = (application) => {
    return normalizeCode(application && (application.code || application.title || application.discount_code));
  };

  const getCartDiscountState = (cart) => {
    const applications = getCartDiscountApplications(cart);

    for (const application of applications) {
      if (!isProductDiscountApplication(application)) continue;

      const valueType = normalize(application.value_type || application.valueType || '');
      const percent = parsePercent(application.value);

      if (valueType === 'percentage' && percent > 0) {
        return {
          code: getApplicationCode(application),
          percent
        };
      }
    }

    return null;
  };

  const syncFromCart = (cart, options) => {
    const opts = normalizeOptions(options);

    if (!cart || typeof cart !== 'object') {
      return readStoredState();
    }

    const discountState = getCartDiscountState(cart);

    if (discountState && discountState.percent > 0) {
      return remember({
        code: normalizeCode(opts.code) || discountState.code,
        percent: discountState.percent,
        source: opts.source || 'cart'
      }, opts);
    }

    const itemCount = Number.parseInt(cart.item_count, 10);

    if (Number.isFinite(itemCount) && itemCount > 0 && opts.clearWhenNoDiscount !== false) {
      const clearOptions = Object.assign({}, opts, {
        source: opts.source || 'cart-no-discount'
      });

      return clear(clearOptions);
    }

    return readStoredState();
  };

  const refreshFromCart = () => {
    if (!config.cartJsonUrl) return;

    fetch(config.cartJsonUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => response.ok ? response.json() : null)
      .then((cart) => {
        if (cart) {
          syncFromCart(cart, { source: 'cart-refresh' });
        }
      })
      .catch(() => {});
  };

  window.YerbaDiscountState = {
    key: STORAGE_KEY,
    eventName: CHANGE_EVENT,
    read: readStoredState,
    remember,
    clear,
    syncFromCart,
    refreshFromCart
  };

  CART_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const cart = event.detail && event.detail.cart;
      if (cart) {
        syncFromCart(cart, { source: eventName });
      }
    });
  });

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event && event.detail;
    if (!detail) return;
    const type = detail.type || detail.action || detail.event || '';
    const stepName = String(detail.stepName || detail.step_name || detail.step || '').toLowerCase();
    const isSuccess = type === 'submit' || type === 'stepSubmit' || type === 'redirectToUrl' || type === 'formRedirect' || type === 'formSuccess' || stepName.includes('success') || stepName.includes('coupon') || stepName.includes('thank');
    if (isSuccess) {
      rememberNewsletterCouponEligibility();
      document.dispatchEvent(new CustomEvent('yerba:newsletter-coupon-eligible', { detail }));
    }
  });

  refreshFromCart();
})();
